/**
 * Ausblendregel „Zeitstrahl aufraeumen" (Asana 1220057765558626, Folie 10; Spec
 * `docs/superpowers/specs/2026-10-30-zeitstrahl-aufraeumen-design.md`): eine Bestellung, die bei ABSINA
 * angeliefert ist (`alleErreicht`) und deren Anlieferung mehr als `AUSBLENDEN_NACH_TAGEN` Kalendertage
 * zurueckliegt, verschwindet aus der Liste; `ZeitstrahlSeite` hebt die Regel bei einer Suche auf.
 */
import { SHIP_STAGES } from "../../config/shipping.ts";
import { localIsoDay } from "../../lib/format.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import type { Order } from "../../lib/types.ts";
import { stufeFuerStatus } from "../../lib/zeitstrahlStufen.ts";
import type { Spurfortschritt } from "./Stationsspur.tsx";

export const AUSBLENDEN_NACH_TAGEN = 7;

/** Kalendertag `YYYY-MM-DD`: ein reines Datum bleibt unveraendert (nie ueber UTC), sonst Ortstag; leer/unlesbar → "". */
const tag = (wert: unknown): string => {
  const text = String(wert ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : localIsoDay(text);
};

/**
 * Der Tag der Anlieferung bei ABSINA: erst der Zeitpunkt der letzten Stufe (DSV-Station, Handfeld
 * unter ABSINA, Statuswechsel), sonst der fruehste lesbare `statusErstmals`-Wert eines Status dieser
 * Stufe - so zaehlt bei Flug/Strasse mit leerem Handfeld der Tag des Statuswechsels. Sonst `null`.
 */
export function anliefertag(
  order: Order,
  fortschritt: Spurfortschritt,
  definitionen: StatusDefinitionen,
): string | null {
  const letzte = SHIP_STAGES.length - 1;
  const direkt = tag(fortschritt.zeitpunkte[letzte]);
  if (direkt) return direkt;
  const tage = Object.entries(order.statusErstmals ?? {})
    .filter(([status]) => stufeFuerStatus(status, definitionen) === letzte)
    .map(([, zeitpunkt]) => tag(zeitpunkt))
    .filter(Boolean)
    .sort();
  return tage[0] ?? null;
}

/** `true`, wenn die Bestellung angeliefert ist und ihr Anliefertag vor dem Ortstag `heute − 7` liegt. */
export function langeAngeliefert(
  order: Order,
  fortschritt: Spurfortschritt,
  definitionen: StatusDefinitionen,
  heute: Date,
): boolean {
  if (!fortschritt.alleErreicht) return false;
  const angeliefert = anliefertag(order, fortschritt, definitionen);
  if (!angeliefert) return false;
  // Kalenderarithmetik mit lokalen Gettern: Sommerzeit verschiebt den Tag nicht.
  const grenze = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - AUSBLENDEN_NACH_TAGEN);
  return angeliefert < localIsoDay(grenze);
}
